import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import QuotesTable from './QuotesTable';
import CommonLoader from '../common/CommonLoader';
import { NotificationManager } from 'react-notifications';
import {
    Button,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Grid,
    Tooltip,
    Chip,
    Box,
    InputAdornment,
} from '@mui/material';
import { Business as BusinessIcon, Edit as EditIcon } from '@mui/icons-material';
import ClientSelectorDialog from '../common/ClientSelectorDialog';
import { GetQuotes } from '../../services/QuotesService';
import { quoteStatusList } from '../common/statusLists';
const clientTypeOptions = [
    'Not Specified',
    'Pool',
    'ETK',
    'ETK Distributor',
    'Drinking Water NonReg',
    'Multiple',
    'FarmOut',
    'OutdoorPool',
    'PoolManagement',
    'Drinking Water',
    'ETK Wholesale',
    'Waste Water',
    'IndoorPool',
    'BathingBeach'
];


const Quotes = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [isViewQuoteMode, setIsViewQuoteMode] = useState(false);
    const [quotesData, setQuotesData] = useState(null);
    const [quotesCount, setQuotesCount] = useState(0);
    const [clientDialogOpen, setClientDialogOpen] = useState(false);
    const [selectedClients, setSelectedClients] = useState([]);
    const [searchCriteria, setSearchCriteria] = useState({
        searchText: '',
        clientType: '',
        orderStatus: '',
        accountNumbers: '',
        startDate: '',
        endDate: '',
        pageNumber: 0,
        pageSize: 10,
    });

    useEffect(() => {
        const today = new Date();
        // ENB-594: "Jump To Quotes" from an order or invoice passes ?account=,
        // pre-select that client (exact account match) over the last 2 years.
        const account = (searchParams.get('account') || '').trim();
        if (account) {
            setSelectedClients([{ accountNumber: account, clientName: '' }]);
        }
        const startDateValue = new Date(today);
        if (account) {
            startDateValue.setFullYear(today.getFullYear() - 2);
        } else {
            startDateValue.setDate(today.getDate() - 15);
        }
        const endDateValue = new Date(today);
        endDateValue.setDate(today.getDate() + 15);

        const initialCriteria = {
            searchText: '',
            clientType: '',
            orderStatus: '',
            accountNumbers: account,
            startDate: startDateValue.toISOString().split('T')[0],
            endDate: endDateValue.toISOString().split('T')[0],
            pageNumber: 0,
            pageSize: 10,
        };
        setSearchCriteria(initialCriteria);
        getQuotes(initialCriteria);
    }, []);

    const getQuotes = async (searchCriteria) => {
        try {
            setLoading(true);
            let response = await GetQuotes(
                searchCriteria.startDate,
                searchCriteria.endDate,
                searchCriteria.orderStatus,
                searchCriteria.clientType,
                searchCriteria.searchText,
                searchCriteria.pageNumber,
                searchCriteria.pageSize,
                searchCriteria.accountNumbers
            );
            setQuotesData(response.results);
            setQuotesCount(response.count);
        } catch (err) {
            NotificationManager.error('Error getting quotes. ' + err, 'Error');
        } finally {
            setLoading(false);
        }
    };

    // Same client picker behavior as Lab Orders search.
    const handleClientSelect = (clients) => {
        if (clients && clients.length > 0) {
            setSelectedClients(clients);
            setSearchCriteria({ ...searchCriteria, accountNumbers: clients.map(c => c.accountNumber.trim()).join(',') });
        }
    };

    const handleRemoveClient = (accountNumber) => {
        const updatedClients = selectedClients.filter(c => c.accountNumber !== accountNumber);
        setSelectedClients(updatedClients);
        setSearchCriteria({ ...searchCriteria, accountNumbers: updatedClients.map(c => c.accountNumber.trim()).join(',') });
    };

    const onViewModeChanged = () => {
        setIsViewQuoteMode(!isViewQuoteMode);
    };

    const handleSearchClick = () => {
        if (searchCriteria.startDate === '' || searchCriteria.endDate === '') {
            NotificationManager.error('Invalid Dates');
            return;
        }
        const updatedSearchCriteria = {
            ...searchCriteria,
            pageNumber: 0,
        };
        setSearchCriteria(updatedSearchCriteria);
        getQuotes(updatedSearchCriteria);
    };

    const onChangePageSettings = async (pageNumber, pageSize) => {
        const updatedSearchCriteria = {
            ...searchCriteria,
            pageNumber: pageNumber,
            pageSize: pageSize,
        };
        setSearchCriteria(updatedSearchCriteria);
        getQuotes(updatedSearchCriteria);
    };

    const handleClientTypeChange = (event) => {
        const value = event.target.value;
        // Convert array to comma-separated string
        const clientTypeString = Array.isArray(value) ? value.join(', ') : value;
        setSearchCriteria({ ...searchCriteria, clientType: clientTypeString });
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();

        if (searchCriteria.startDate === '' || searchCriteria.endDate === '') {
            NotificationManager.error('Invalid Dates');
            return;
        }

        const updatedSearchCriteria = {
            ...searchCriteria,
            pageNumber: 0,
        };

        setSearchCriteria(updatedSearchCriteria);
        getQuotes(updatedSearchCriteria);
    };


    // Convert comma-separated string to array for the Select component
    const getClientTypeArray = () => {
        if (!searchCriteria.clientType) return [];
        return searchCriteria.clientType.split(', ').filter(type => type.trim() !== '');
    };

    return (
        <div className="vlp-component-container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="title">Quotes</span>

                <Button
                    variant="contained"
                    size="small"
                    style={{ marginRight: "2%" }}
                    onClick={() => (window.location.href = "/vlp/quotes/add")}
                >
                    Add Quote
                </Button>
            </div>

            <hr />

            {!isViewQuoteMode && (
                <div className="columns">
                    <div className="column">
                        <form onSubmit={handleSearchSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm>
                                    <Tooltip
                                        title="Search by quote number, account number, billing name, billing contact, description, quoted amount, or list price"
                                        arrow
                                        placement="top"
                                    >
                                        <TextField
                                            label="Search Criteria"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            value={searchCriteria.searchText}
                                            onChange={(e) => setSearchCriteria({ ...searchCriteria, searchText: e.target.value })}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm>
                                    <FormControl variant="outlined" fullWidth size="small">
                                        <InputLabel>Client Type</InputLabel>
                                        <Select
                                            multiple
                                            value={getClientTypeArray()}
                                            onChange={handleClientTypeChange}
                                            label="Client Type"
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => (
                                                        <Chip key={value} label={value} size="small" />
                                                    ))}
                                                </Box>
                                            )}
                                        >
                                            {clientTypeOptions.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm>
                                    <FormControl variant="outlined" fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={searchCriteria.orderStatus}
                                            onChange={(e) => setSearchCriteria({ ...searchCriteria, orderStatus: e.target.value })}
                                            label="Status"
                                        >
                                            <MenuItem value="">
                                                <em>Select Any Status</em>
                                            </MenuItem>
                                            {quoteStatusList.map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    {status}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm>
                                    <Tooltip
                                        title="Select one or multiple account numbers (clients), exact match"
                                        arrow
                                        placement="top"
                                    >
                                        <TextField
                                            label="Client Account Numbers"
                                            value={
                                                selectedClients.length > 0
                                                    ? `${selectedClients.length} selected`
                                                    : ''
                                            }
                                            placeholder="Click to select clients"
                                            size="small"
                                            fullWidth
                                            onClick={() => setClientDialogOpen(true)}
                                            InputProps={{
                                                readOnly: true,
                                                style: { cursor: 'pointer' },
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <BusinessIcon color={selectedClients.length > 0 ? "success" : "primary"} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: selectedClients.length > 0 ? (
                                                    <InputAdornment position="end">
                                                        <EditIcon color="primary" sx={{ fontSize: 20 }} />
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm>
                                    <Tooltip
                                        title="Filter by date created, expiration date, or date set"
                                        arrow
                                        placement="top"
                                    >
                                        <TextField
                                            label="From Date"
                                            type="date"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            value={searchCriteria.startDate}
                                            onChange={(e) => setSearchCriteria({ ...searchCriteria, startDate: e.target.value })}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm>
                                    <Tooltip
                                        title="Filter by date created, expiration date, or date set"
                                        arrow
                                        placement="top"
                                    >
                                        <TextField
                                            label="To Date"
                                            type="date"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            value={searchCriteria.endDate}
                                            onChange={(e) => setSearchCriteria({ ...searchCriteria, endDate: e.target.value })}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm="auto">
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                    >
                                        Search
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>

                        {selectedClients.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <strong style={{ fontSize: '0.875rem' }}>Selected Clients:</strong>
                                {selectedClients.map((client) => (
                                    <Chip
                                        key={client.accountNumber}
                                        label={client.clientName ? `${client.accountNumber} - ${client.clientName}` : client.accountNumber}
                                        onDelete={() => handleRemoveClient(client.accountNumber)}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                ))}
                            </Box>
                        )}
                    </div>
                </div>
            )}

            <ClientSelectorDialog
                open={clientDialogOpen}
                onClose={() => setClientDialogOpen(false)}
                onSelect={handleClientSelect}
                multiSelect={true}
            />

            {quotesData !== null && (
                <QuotesTable
                    quotesData={quotesData}
                    count={quotesCount}
                    onChange={onChangePageSettings}
                    onViewModeChanged={onViewModeChanged}
                />
            )}
            <CommonLoader loading={loading} />
        </div>
    );
};

export default Quotes;